import AddPatient from "./add-patient";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableLoading,
  TableRow,
} from "@/components/ui/table";
import { Route as patientRoute } from "@/routes/(pharma)/_layout/patients/index";
import {
  CaretSortIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DoubleArrowLeftIcon,
  DoubleArrowRightIcon,
} from "@radix-ui/react-icons";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
dayjs.extend(customParseFormat);
import { useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { MoreHorizontal, SearchIcon, MoveRightIcon } from "lucide-react";
import { tableHeadClass, tableCellClass, tableCellLastRowClass } from "@/components/ui/table-styles";
import { cn } from "@/lib/utils";
import PaginationButton from "@/components/ui/pagination-button";
import { usePatientsData } from "../-hooks/usePatientsData";

const Component = () => {
  const searchParams = patientRoute.useSearch();
  const navigate = useNavigate();
  const [search, setSearch] = useState(searchParams.search || '');

    // Use the custom hook for data fetching
    const { data: patients, isLoading, error, isFetching } = usePatientsData();

    // Local loading state for search actions
    const [isSearching, setIsSearching] = useState(false);
  
    // Reset search loading when query completes
    useEffect(() => {
      if (!isFetching && isSearching) {
        setIsSearching(false);
      }
    }, [isFetching, isSearching]);
  
    // Helper function to update search params without full navigation
    const updateSearchParams = (updates: Partial<typeof searchParams>, showSearchLoading = false) => {
      if (showSearchLoading) {
        setIsSearching(true);
      }
      
      navigate({
        search: (prev: any) => ({ ...prev, ...updates }),
        replace: true, // This prevents creating new history entries
      });
    };

  const [order, setOrder] = useState("asc");
  const [orderBy, setOrderBy] = useState("created_at");

  const handleRequestSort = (property: string) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  
    // Sorting data (handle loading state)
  const sortedData = patients?.patients ? patients.patients.slice().sort((a, b) => {
    if (orderBy === "createdAt" || orderBy === "lastVisitedDate") {
      const getTimestampValue = (item: any, key: string): number => {
        const timestamp = item[key];
        
        // Handle undefined/null
        if (!timestamp) return 0;
        
        // Protobuf Timestamp has a toDate() method
        if (timestamp.toDate) {
          return timestamp.toDate().getTime();
        }
        
        // Or if it has seconds property (protobuf Timestamp structure)
        if (timestamp.seconds) {
          return Number(timestamp.seconds) * 1000;
        }
        
        // Fallback for string dates
        if (typeof timestamp === "string") {
          return new Date(timestamp).getTime();
        }
        
        return 0;
      };
      const dateA = getTimestampValue(a, orderBy)
      const dateB = getTimestampValue(b, orderBy);
      return order === "asc" ? dateA - dateB : dateB - dateA;
    }
    return 0;
  }) : [];
  
  const totalPages = patients && patients.patients.length > 0 ? Math.ceil(patients.totalCount! / (searchParams.pageSize || 10)) : 1;

  // TableCell and TableHead wrappers for consistent styling
  const StyledTableCell = ({ className = "", ...props }) => (
    <TableCell className={cn(tableCellClass, "py-2.5 font-medium", className)} {...props} />
  );
  const StyledTableHead = ({ className = "", ...props }) => (
    <TableHead className={cn(tableHeadClass, className)} {...props} />
  );

  interface Column {
    key: string;
    label: string;
  }

  const columns: Column[] = [
    {key: "name", label: "Name"},
    {key: "phone", label: "Phone"},
    {key: "emirates_id", label: "Emirated ID"},
    {key: "last_visited", label: "Last Visit"},
    {key: "created_at", label: "Created At"},
    {key: "status", label: "Status"},
  ];

  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    columns.reduce((acc, col) => ({ ...acc, [col.key]: true }), {} as Record<string, boolean>)
  );

  return (
    <>
      <div className="overflow-y-scroll h-screen pb-48">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold tracking-tight">Patients</h2>
            <p className="text-muted-foreground">
              Here is a list of all your patients.
            </p>
          </div>
          <AddPatient />
        </div>
        <hr className="my-4" />
        <div className="flex items-center gap-x-2">
          <div className="relative w-72">
            <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400">
              <SearchIcon className="w-4 h-4" />
            </span>
            <Input
              placeholder="Search Patients..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  updateSearchParams({
                    search: search,
                    page: 1,
                  }, true);
                }
              }}
              className="pl-8 pr-8 w-full"
            />
            {search && (
              <Button
                variant="ghost"
                className="absolute right-1 top-1/2 -translate-y-1/2 p-1 h-6 w-6 text-gray-400 hover:text-gray-700"
                onClick={() => {
                  setSearch("");
                  updateSearchParams({
                    search: "",
                    page: 1,
                  });
                }}
              >
                <span className="sr-only">Clear</span>
                &#10005;
              </Button>
            )}
          </div>
          <Button
            variant="outline"
            disabled={isSearching || isLoading}
            onClick={() => {
              updateSearchParams({
                search: search,
                page: 1,
              }, true);
            }}
          >
            {isSearching || isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-current border-t-transparent"></div>
                Searching...
              </div>
            ) : (
              'Search'
            )}
          </Button>
        </div>
        <div className="mt-4 border rounded-lg bg-white overflow-hidden">
        {isLoading ? (
              <TableLoading columns={columns} visibleColumns={visibleColumns} />
            ) : !patients?.patients || patients.patients.length === 0 ? (
                <div className="flex items-center justify-center p-8">
                  <div className="text-sm text-gray-500">No entries found</div>
                </div>
              ) : (
                <>
          <Table className="text-sm">
            <TableHeader>
              <TableRow>
                <StyledTableHead>Name</StyledTableHead>
                <StyledTableHead>Phone</StyledTableHead>
                <StyledTableHead>Emirates ID</StyledTableHead>
                <StyledTableHead>Last Vist</StyledTableHead>
                <StyledTableHead
                  onClick={() => handleRequestSort("created_at")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="flex items-center">
                    <span>Created At</span>
                    {orderBy === "created_at" && (
                      <CaretSortIcon
                        className={`ml-2 h-4 w-4 ${order === "asc" ? "rotate-180" : ""}`}
                      />
                    )}
                  </div>
                </StyledTableHead>
                <StyledTableHead>Status</StyledTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((patient, index) => (
                <TableRow key={patient.patientId} className="cursor-pointer" onClick={() => {
                  navigate({
                    to: `/patients/patient-info/$patientId`,
                    params: { patientId: patient.patientId },
                  });
                }}>
                  <StyledTableCell>{patient.name}</StyledTableCell>
                  <StyledTableCell>{patient.phone}</StyledTableCell>
                  <StyledTableCell>{patient.govtIdentifier}</StyledTableCell>
                  <StyledTableCell>
                    {dayjs(patient.lastVisited?.toDate()).isBefore(dayjs("2088-01-01"))
                          ? dayjs(patient.lastVisited?.toDate()).format("DD MMM, YYYY HH:mm")
                      : "Never Visited"}
                  </StyledTableCell>
                  <StyledTableCell>
                    {dayjs(patient.createdAt?.toDate()).format("DD MMM, YYYY")}
                  </StyledTableCell>
                  <StyledTableCell>{patient.status}</StyledTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <div className="flex items-center justify-between py-4 px-6 text-sm border-t bg-white">
            <p>{patients?.patients && patients.patients.length > 0 && patients.totalCount ? patients.totalCount : 0} <span className="text-gray-500 font-light">count</span></p>
            <div className="flex items-center gap-1">
              <PaginationButton
                  onClick={() => {
                    updateSearchParams({
                      page: 1
                    });
                  }}
                disabled={searchParams.page === 1}
              >
                <DoubleArrowLeftIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                    onClick={() => {
                      updateSearchParams({
                        page: searchParams.page - 1
                      });
                    }}
                disabled={searchParams.page === 1}
              >
                <ChevronLeftIcon className="h-4 w-4" />
              </PaginationButton>
              {(() => {
                const getPageNumbers = () => {
                  const pages = [];
                  if (totalPages <= 7) {
                    for (let i = 1; i <= totalPages; i++) {
                      pages.push(i);
                    }
                  } else {
                    pages.push(1);
                    if (searchParams.page > 3) pages.push('...');
                    for (let i = Math.max(2, searchParams.page - 1); i <= Math.min(searchParams.page + 1, totalPages - 1); i++) {
                      pages.push(i);
                    }
                    if (searchParams.page < totalPages - 2) pages.push('...');
                    pages.push(totalPages);
                  }
                  return pages;
                };
                return getPageNumbers().map((p, idx) => (
                  p === '...'
                    ? <span key={`ellipsis-${idx}`} className="mx-1">...</span>
                    : <PaginationButton
                        key={p}
                        active={searchParams.page === p}
                        onClick={() => {
                          updateSearchParams({
                            page: p as number
                          });
                        }}
                      >
                        <span className="text-sm">{p}</span>
                      </PaginationButton>
                ));
              })()}
              <PaginationButton
                onClick={() => {
                  updateSearchParams({
                    page: searchParams.page + 1
                  });
                }}
                disabled={searchParams.page === totalPages}
              >
                <ChevronRightIcon className="h-4 w-4" />
              </PaginationButton>
              <PaginationButton
                onClick={() => {
                  updateSearchParams({
                      page: totalPages
                  });
                }}
                disabled={searchParams.page === totalPages}
              >
                <DoubleArrowRightIcon className="h-4 w-4" />
              </PaginationButton>
            </div>
          </div>
          </>
          )}
        </div>
      </div>
    </>
  );
};

export default Component;
